// Polyfills must be imported first (before any other imports)
import './polyfills';

import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App.tsx';
import { initElectronLogger } from './utils/electronLogger';

// Initialize Electron renderer logging (captures console.error, unhandled errors)
// No-op in non-Electron environments (browser/cloud)
initElectronLogger();

// Sentry error tracking - no-op without DSN (local dev)
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.VITE_ENVIRONMENT || 'development',
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 0.1,
    tracePropagationTargets: ['localhost', /^https:\/\/aigod-api/],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);

// Microsoft Clarity - heatmaps + session replay (production only)
const clarityId = import.meta.env.VITE_CLARITY_PROJECT_ID;
if (clarityId) {
  (function (c: Record<string, unknown> & Window, l: Document, a: string, r: string, i: string) {
    const cRec = c as Record<string, { q?: unknown[][] } & ((...args: unknown[]) => void)>;
    cRec[a] =
      cRec[a] ||
      function (...args: unknown[]) {
        (cRec[a].q = cRec[a].q || []).push(args);
      };
    const t = l.createElement(r) as HTMLScriptElement;
    t.async = true;
    t.src = 'https://www.clarity.ms/tag/' + i;
    const y = l.getElementsByTagName(r)[0];
    y.parentNode?.insertBefore(t, y);
  })(
    window as unknown as Record<string, unknown> & Window,
    document,
    'clarity',
    'script',
    clarityId
  );
}
